import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { ThreadModerationList } from '@/components/admin/corkBoard/ThreadModerationList';
import { DesktopEditThreadTagsPanel } from '@/components/admin/corkBoard/desktop/DesktopEditThreadTagsPanel';
import { DesktopAdminPostPanel } from '@/components/admin/corkBoard/desktop/DesktopAdminPostPanel';
import { AdminSurveyEditModal } from '@/components/admin/corkBoard/AdminSurveyEditModal';
import { GlassCard } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { useThreadModeration } from '@/hooks/admin/useThreadModeration';
import type { CorkBoardThreadSummary, CreateSurveyThreadRequest } from '@/types/residents/corkBoard';

interface DesktopSurveysManagementViewProps {
  moderation: ReturnType<typeof useThreadModeration>;
  postModalVisible: boolean;
  editTagsVisible: boolean;
  editingThread: CorkBoardThreadSummary | null;
  onOpenEditTags: (thread: CorkBoardThreadSummary) => void;
  onCloseEditTags: () => void;
  onSaveTags: (threadId: string, tagIds: number[]) => void;
  onOpenPostModal: () => void;
  onClosePostModal: () => void;
  onSaveSurvey: (data: Omit<CreateSurveyThreadRequest, 'propertyId'>) => void;
  onDelete: (thread: CorkBoardThreadSummary) => void;
  onViewResponses: (thread: CorkBoardThreadSummary) => void;
  onEdit: (thread: CorkBoardThreadSummary) => void;
  onClose: (thread: CorkBoardThreadSummary) => void;
  editSurveyVisible: boolean;
  editingSurvey: CorkBoardThreadSummary | null;
  onCloseEditSurvey: () => void;
  onSaveSurveyEdit: (data: { threadId: string; title: string; body: string; tagIds: number[] }) => void;
  onSurveyConfigChange: (patch: { threadId: string; expiresAt?: string | null; allowReplies?: boolean }) => void;
}

export const DesktopSurveysManagementView: React.FC<DesktopSurveysManagementViewProps> = ({
  moderation,
  postModalVisible,
  editTagsVisible,
  editingThread,
  onOpenEditTags,
  onCloseEditTags,
  onSaveTags,
  onOpenPostModal,
  onClosePostModal,
  onSaveSurvey,
  onDelete,
  onViewResponses,
  onEdit,
  onClose,
  editSurveyVisible,
  editingSurvey,
  onCloseEditSurvey,
  onSaveSurveyEdit,
  onSurveyConfigChange,
}) => {
  const { redesign, semantic } = useAdminColors();

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Surveys"
        subtitle="Create surveys and manage them: pin, lock, delete, and view responses."
        showAddButton
        onAddPress={onOpenPostModal}
        addButtonText="New Survey"
      />

      <View style={styles.content}>
        {moderation.error && (
          <View
            style={[
              styles.errorBanner,
              { backgroundColor: redesign.accent.redBg, borderColor: semantic.status.error },
            ]}
          >
            <Text style={[styles.errorBannerText, { color: semantic.status.error }]}>
              Failed to load surveys.
            </Text>
            <TouchableOpacity onPress={moderation.refetch}>
              <Text style={[styles.errorRetryText, { color: semantic.status.error }]}>Retry</Text>
            </TouchableOpacity>
          </View>
        )}

        <GlassCard padding={0} style={styles.card}>
          <View style={[styles.cardHeader, { borderBottomColor: redesign.card.border }]}>
            <Text style={[styles.cardTitle, { color: redesign.text.primary }]}>Surveys</Text>
            <Text style={[styles.cardSubtitle, { color: redesign.text.secondary }]}>
              {moderation.totalCount} survey{moderation.totalCount !== 1 ? 's' : ''}
            </Text>
          </View>

          <ThreadModerationList
            threads={moderation.threads}
            isLoading={moderation.isLoading}
            isMutating={moderation.isMutating}
            onPin={moderation.handlePinThread}
            onLock={moderation.handleLockThread}
            onEditTags={onOpenEditTags}
            onDelete={onDelete}
            onViewResponses={onViewResponses}
            onEdit={onEdit}
            onClose={onClose}
            onRefresh={moderation.refetch}
            isRefreshing={moderation.isManualRefreshing}
            onLoadMore={moderation.loadMore}
            hasNextPage={moderation.hasNextPage}
            isFetchingMore={moderation.isFetchingNextPage}
          />
        </GlassCard>
      </View>

      <DesktopEditThreadTagsPanel
        visible={editTagsVisible}
        thread={editingThread}
        availableTags={moderation.tags}
        isSaving={moderation.isUpdatingTags}
        onSave={onSaveTags}
        onClose={onCloseEditTags}
      />

      <DesktopAdminPostPanel
        visible={postModalVisible}
        kind="survey"
        availableTags={moderation.tags}
        isSavingSurvey={moderation.isCreatingSurvey}
        onSaveSurvey={onSaveSurvey}
        onClose={onClosePostModal}
      />

      <AdminSurveyEditModal
        visible={editSurveyVisible}
        thread={editingSurvey}
        availableTags={moderation.tags}
        isSaving={moderation.isUpdatingSurvey}
        onSave={onSaveSurveyEdit}
        onConfigChange={onSurveyConfigChange}
        onViewResponses={onViewResponses}
        onClose={onCloseEditSurvey}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    gap: 16,
  },
  card: {
    flex: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  cardSubtitle: {
    fontSize: 13,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  errorBannerText: {
    fontSize: 13,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
